const DATA_STATE_ALIASES = {
  ok: "ok",
  full: "ok",
  fresh: "ok",
  healthy: "ok",
  green: "ok",
  ready: "ok",
  active: "ok",
  stable: "ok",
  stale: "stale",
  outdated: "stale",
  expired: "stale",
  old: "stale",
  empty: "empty",
  none: "empty",
  missing: "empty",
  no_data: "empty",
  unavailable: "unavailable",
  not_available: "unavailable",
  error: "unavailable",
  failed: "unavailable",
  down: "unavailable",
  degraded: "degraded",
  partial: "degraded",
  warning: "degraded",
  warn: "degraded",
  limited: "degraded",
  insufficient: "degraded",
  processing: "processing",
  loading: "processing",
  pending: "processing",
  queued: "processing",
  running: "processing",
};

const DATA_STATE_PRESENTATION = {
  ok: {
    label: "Frisch",
    badgeClass: "border-emerald-400/25 bg-emerald-500/10 text-emerald-200",
    panelClass: "border-emerald-400/20 bg-emerald-500/[0.06]",
    textClass: "text-emerald-200",
  },
  stale: {
    label: "Veraltet",
    badgeClass: "border-amber-400/25 bg-amber-500/10 text-amber-200",
    panelClass: "border-amber-400/20 bg-amber-500/[0.08]",
    textClass: "text-amber-200",
  },
  empty: {
    label: "Leer",
    badgeClass: "border-slate-400/20 bg-slate-500/10 text-slate-300",
    panelClass: "border-slate-500/20 bg-slate-800/40",
    textClass: "text-slate-300",
  },
  unavailable: {
    label: "Nicht verfügbar",
    badgeClass: "border-rose-400/25 bg-rose-500/10 text-rose-200",
    panelClass: "border-rose-400/20 bg-rose-500/[0.08]",
    textClass: "text-rose-200",
  },
  degraded: {
    label: "Eingeschränkt",
    badgeClass: "border-amber-400/25 bg-amber-500/10 text-amber-200",
    panelClass: "border-amber-400/20 bg-amber-500/[0.08]",
    textClass: "text-amber-200",
  },
  processing: {
    label: "In Arbeit",
    badgeClass: "border-cyan-400/25 bg-cyan-500/10 text-cyan-200",
    panelClass: "border-cyan-400/20 bg-cyan-500/[0.08]",
    textClass: "text-cyan-200",
  },
};

function normalizeKey(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function parseTimestamp(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function normalizeDataState(value) {
  const key = normalizeKey(value);
  return DATA_STATE_ALIASES[key] ?? null;
}

export function getDataStatePresentation(state) {
  const key = normalizeDataState(state) ?? "ok";
  return DATA_STATE_PRESENTATION[key];
}

export function formatDataTimestamp(value) {
  const date = parseTimestamp(value);
  if (!date) return null;
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function resolveDataState({
  status,
  metaStatus,
  error = null,
  updatedAt = null,
  lastFetchedAt = null,
  staleAfterMs = null,
  itemCount = null,
} = {}) {
  const timestamp = parseTimestamp(updatedAt ?? lastFetchedAt);
  const timestampLabel = formatDataTimestamp(timestamp);
  const hasExplicitState = Boolean(normalizeDataState(status) || normalizeDataState(metaStatus));
  const isStaleByAge = Boolean(
    timestamp
    && Number.isFinite(staleAfterMs)
    && staleAfterMs > 0
    && (Date.now() - timestamp.getTime()) > staleAfterMs,
  );

  let state = error
    ? "unavailable"
    : normalizeDataState(status)
      ?? normalizeDataState(metaStatus);

  if (!state && itemCount !== null && itemCount <= 0) {
    state = "empty";
  }
  if (!state) {
    state = "ok";
  }
  if ((state === "ok" || (!hasExplicitState && state === "empty")) && isStaleByAge) {
    state = "stale";
  }

  return {
    state,
    timestamp,
    timestampLabel,
    isStale: state === "stale",
    isEmpty: state === "empty",
    hasExplicitState,
  };
}

export function buildDataStateDescription(state, subject = "Daten", { timestampLabel = null } = {}) {
  const key = normalizeDataState(state) ?? "ok";
  switch (key) {
    case "stale":
      return `${subject} sind veraltet${timestampLabel ? ` (letzter Stand ${timestampLabel})` : ""}.`;
    case "empty":
      return `${subject} sind derzeit leer und liefern noch keine verwertbaren Inhalte.`;
    case "unavailable":
      return `${subject} konnten aktuell nicht geladen werden.`;
    case "degraded":
      return `${subject} sind nur teilweise verfügbar und sollten mit Vorsicht interpretiert werden.`;
    case "processing":
      return `${subject} werden gerade aktualisiert oder nachberechnet.`;
    default:
      return `${subject} sind verfügbar.`;
  }
}

export function isMissingDescriptor(value) {
  const key = normalizeKey(value);
  return !key || ["undefined", "null", "n_a", "na", "unknown", "—"].includes(key);
}
